import { useCallback, useEffect, useState } from "react";
import { axiosClient } from "../configs/axiosClient";
import { API } from "../configs/endpoints";
import { toast } from "react-toastify";
import { getErrorMessage } from "../utils/errorHandler";
import { TiPencil } from "react-icons/ti";
import { RiDeleteBinLine } from "react-icons/ri";
import { ConfirmModal } from "../components/common/ConfirmModal";

type ReviewItem = {
  _id: string;
  title: string;
  productId?: { _id: string; name: string; slug: string };
  linkVideo: string;
  isActive: boolean;
  sortOrder: number;
};

type ProductOption = { _id: string; name: string };

const defaultForm = {
  title: "",
  productId: "",
  linkVideo: "",
  isActive: true,
  sortOrder: 0,
};

export const Review = () => {
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      const { data } = await axiosClient.get(API.review.list);
      setReviews(data.reviews ?? []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axiosClient.get(API.product.list, {
        params: { limit: 200 },
      });
      setProducts(
        (data.products ?? []).map((p: { _id: string; name: string }) => ({
          _id: p._id,
          name: p.name,
        })),
      );
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchReviews();
    fetchProducts();
  }, [fetchReviews, fetchProducts]);

  const openCreate = () => {
    setEditId(null);
    setForm(defaultForm);
    setShowForm(true);
  };

  const openEdit = (r: ReviewItem) => {
    setEditId(r._id);
    setForm({
      title: r.title,
      productId: r.productId?._id ?? "",
      linkVideo: r.linkVideo,
      isActive: r.isActive,
      sortOrder: r.sortOrder,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.productId || !form.linkVideo) {
      toast.warning("Vui lòng điền đầy đủ thông tin");
      return;
    }
    setLoading(true);
    try {
      if (editId) {
        await axiosClient.put(API.review.update(editId), form);
        toast.success("Cập nhật review thành công");
      } else {
        await axiosClient.post(API.review.create, form);
        toast.success("Thêm review thành công");
      }
      setShowForm(false);
      fetchReviews();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axiosClient.delete(API.review.delete(id));
      toast.success("Xóa review thành công");
      fetchReviews();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="p-5 space-y-2">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Quản lý Review sản phẩm
        </h1>
        <button
          type="button"
          onClick={openCreate}
          className="p-2 text-sm font-medium text-white bg-blue-500 rounded-lg cursor-pointer hover:bg-blue-600 transition-colors duration-300"
        >
          Thêm review
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200">
        <div className="w-full overflow-x-auto scrollbar-custom">
          <table className="w-full min-w-3xl">
            <thead>
              <tr className="bg-gray-100 divide-x divide-gray-200 border border-gray-200">
                <th className="text-sm font-semibold text-gray-700 p-2 text-center">
                  Tiêu đề
                </th>
                <th className="text-sm font-semibold text-gray-700 p-2 text-center">
                  Sản phẩm
                </th>
                <th className="text-sm font-semibold text-gray-700 p-2 text-center">
                  Video
                </th>
                <th className="text-sm font-semibold text-gray-700 p-2 text-center">
                  Trạng thái
                </th>
                <th className="text-sm font-semibold text-gray-700 p-2 text-center">
                  Thứ tự
                </th>
                <th className="text-sm font-semibold text-gray-700 p-2 text-center">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody>
              {reviews.length > 0 ? (
                reviews.map((r) => (
                  <tr
                    key={r._id}
                    className="divide-x border border-gray-200 divide-gray-200"
                  >
                    <td className="text-sm text-gray-700 p-2 text-center">
                      {r.title}
                    </td>
                    <td className="text-sm text-gray-700 p-2 text-center">
                      {r.productId?.name ?? "—"}
                    </td>
                    <td className="text-sm text-blue-500 p-2 text-center max-w-40 truncate">
                      <a
                        href={r.linkVideo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {r.linkVideo}
                      </a>
                    </td>
                    <td className="p-2 text-center">
                      <span
                        className={`text-sm ${r.isActive ? "text-green-500" : "text-red-500"}`}
                      >
                        {r.isActive ? "Hiện" : "Ẩn"}
                      </span>
                    </td>
                    <td className="text-sm text-gray-700 p-2 text-center">
                      {r.sortOrder}
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-2 justify-center">
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="cursor-pointer text-xl text-gray-700 hover:text-blue-500 transition-colors"
                        >
                          <TiPencil />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(r._id)}
                          className="cursor-pointer text-xl text-gray-700 hover:text-red-500 transition-colors"
                        >
                          <RiDeleteBinLine />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="p-4 text-sm text-gray-400 text-center">
                    Chưa có review nào
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4"
          >
            <h2 className="text-lg font-bold">
              {editId ? "Cập nhật review" : "Thêm review mới"}
            </h2>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Tiêu đề
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full p-2 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                placeholder="Ví dụ: Review máy khoan Bosch GSB 16 RE"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Sản phẩm
              </label>
              <select
                value={form.productId}
                onChange={(e) =>
                  setForm({ ...form, productId: e.target.value })
                }
                className="w-full p-2 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
              >
                <option value="">-- Chọn sản phẩm --</option>
                {products.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Link YouTube Embed
              </label>
              <input
                type="text"
                value={form.linkVideo}
                onChange={(e) =>
                  setForm({ ...form, linkVideo: e.target.value })
                }
                className="w-full p-2 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                placeholder="https://www.youtube.com/embed/..."
              />
            </div>

            <div className="flex gap-4">
              <div className="space-y-1 flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Thứ tự
                </label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) =>
                    setForm({ ...form, sortOrder: Number(e.target.value) })
                  }
                  className="w-full p-2 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Trạng thái
                </label>
                <select
                  value={String(form.isActive)}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.value === "true" })
                  }
                  className="w-full p-2 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                >
                  <option value="true">Hiện</option>
                  <option value="false">Ẩn</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                {loading ? "Đang lưu..." : editId ? "Cập nhật" : "Thêm"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <ConfirmModal
          message="Bạn chắc chắn muốn xóa review này?"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};
